/**
 * DatabaseSelectorDialog - popup for selecting a database from a connection
 * Used when toolbar actions need a database but multiple are available
 */

import { useEffect, useState } from 'react';
import { Check, Database, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { cn } from '../../../lib/utils';
import { useDatabaseList } from '../../database-objects/hooks/useDatabaseList';
import { DatabaseListingFallbackBanner } from '../../connection-diagnostics/components/DatabaseListingFallbackBanner';
import { UnverifiedDatabaseIndicator } from '../../connection-diagnostics/components/UnverifiedDatabaseIndicator';
import { useFallbackPresentation } from '../../connection-diagnostics/hooks/useFallbackPresentation';
import { useDatabaseSelectorNavigation } from '../hooks/useDatabaseSelectorNavigation';
import type { DatabaseEngineId } from '@/core/engines';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type DatabaseSelectorDialogProps = {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog is closed without selection */
  onOpenChange: (open: boolean) => void;
  /** Connection ID to fetch databases from */
  connectionId: string;
  /** Connection name to display in header */
  connectionName: string;
  /** Database engine (defaults to 'mssql') */
  engine?: DatabaseEngineId;
  /** Called when a database is selected */
  onSelect: (database: string, setAsDefault?: boolean) => void;
  /** Show "set as default" checkbox option */
  showSetAsDefault?: boolean;
  /** Explorer's suggested database (pre-selected in list) */
  suggestedDatabase?: string | null;
};

export function DatabaseSelectorDialog({
  open,
  onOpenChange,
  connectionId,
  connectionName,
  engine = 'mssql',
  onSelect,
  showSetAsDefault = false,
  suggestedDatabase,
}: DatabaseSelectorDialogProps) {
  const [setAsDefault, setSetAsDefault] = useState(false);

  // Shared per-connection listing — an already-listed connection paints its
  // databases as soon as the dialog opens, while a revalidation runs behind it.
  //
  // Slice 5b: this dialog used to render `result.databases` on a FAILED listing
  // (the engine's hardcoded fallback list) as real, selectable options as long
  // as the array was non-empty. It now keys on `state` like every sibling
  // picker: only an `'ok'` listing yields a real listing.
  const {
    databases,
    state: listingState,
    isInitialLoading,
    isRefreshing,
  } = useDatabaseList({ connectionId, engine, enabled: open });

  const isListingFailure = listingState === 'error' || listingState === 'empty';
  const fallback = useFallbackPresentation({ connectionId, state: listingState });

  // A failed / empty listing must NOT dead-end the action. This dialog is what a
  // database-gated toolbar action (Table Designer, Data Import, Clone Table …)
  // opens when it cannot resolve a database on its own, and the login that most
  // needs it is precisely the one whose listing fails — no `VIEW ANY DATABASE`,
  // so `getDatabases` returns `state: 'error'` while the user's own database is
  // perfectly reachable. It therefore offers the same affordance as every sibling
  // picker (`FallbackSelectContent`, `SchemaDatabaseDropdown`, the OE tree): the
  // explainer banner with *Show details*, plus `[config.database,
  // ...knownDatabases]` as SELECTABLE rows, each marked unverified. `databases`
  // is still `[]` in that state (Slice 5b — the engine's hardcoded fallback list
  // is never painted as a listing), which is why the rows come from
  // `useFallbackPresentation`.
  //
  // Auto-selection is untouched: `contextSelectionStore` still auto-picks only on
  // `state === 'ok'`, so an unverified entry is only ever used because the user
  // clicked it.
  const options = isListingFailure ? fallback.entries : databases;
  const isLoaded = !isInitialLoading;

  const {
    searchFilter,
    setSearchFilter,
    showSearch,
    filteredDatabases,
    selectedIndex,
    setSelectedIndex,
    itemRefs,
    searchInputRef,
    handleKeyDown,
  } = useDatabaseSelectorNavigation({
    open,
    isLoaded,
    options,
    suggestedDatabase,
    onCommit: onSelect,
  });

  // The checkbox is the one piece of per-open state the navigation hook does not
  // own — it belongs to the *Set as default* rule below.
  useEffect(() => {
    if (open) setSetAsDefault(false);
  }, [open]);

  // *Set as default* PERSISTS the name onto the connection record, and `database`
  // is a dial field (`dialProjection`): `updateConnection` therefore calls
  // `connectionRequiresPoolReset` and marks a still-CONNECTED connection
  // disconnected, so the next connect re-dials the new database. That is right for a
  // database the server just listed, and wrong for an unverified fallback row —
  // picking a `knownDatabases` name that has since been dropped would tear down a
  // perfectly healthy connection with nothing to roll back to. So the unverified
  // rows stay selectable for the action at hand (which is why they exist) and the
  // default is simply not on offer until a listing has verified the name.
  //
  // `isRefreshing` is the same rule over the INTERVAL this change opened up. The
  // dialog now paints from the shared cache and revalidates behind it, so between
  // the open and the revalidation landing `listingState` can be a cached `'ok'`
  // describing a list the server may no longer agree with — and persisting a name
  // that has since been dropped hits exactly the pool-reset chain above. A listing
  // is always a little stale; what this widens is the window, so the checkbox waits
  // for the revalidation in flight to settle. Selecting a database for the action at
  // hand is unaffected: that is reversible, persisting a dial field is not.
  const listingSettled = !isRefreshing;
  const canSetAsDefault = showSetAsDefault && !isListingFailure && listingSettled;

  // Un-tick when the checkbox goes back to disabled (a listing invalidated from
  // elsewhere while the dialog is open), so a ticked box is never silently ignored
  // by `handleSelect`.
  useEffect(() => {
    if (!listingSettled) setSetAsDefault(false);
  }, [listingSettled]);

  const handleSelect = (database: string) => {
    onSelect(database, canSetAsDefault ? setAsDefault : undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[400px]"
        data-testid="database-selector-dialog"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>Select Database</DialogTitle>
          <DialogDescription>
            Choose a database on {connectionName}
          </DialogDescription>
        </DialogHeader>

        {isInitialLoading && (
          <div
            className="flex items-center justify-center py-8"
            data-testid="database-selector-loading"
          >
            <RandomDotmSpinner size={24} className="text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">
              Loading databases...
            </span>
          </div>
        )}

        {isLoaded && (
          <>
            {isListingFailure && (
              <div className="flex flex-col gap-2" data-testid="database-selector-error">
                <DatabaseListingFallbackBanner
                  connectionId={connectionId}
                  variant="block"
                  trigger={listingState === 'empty' ? 'list_empty' : 'list_failure'}
                  data-testid="database-selector-dialog-fallback-banner"
                  showDetailsTestId="database-selector-dialog-show-details"
                  sendTestId="database-selector-dialog-send-diagnostic-report"
                />
              </div>
            )}

            {showSearch && (
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  placeholder="Filter databases..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-8"
                  data-testid="database-search-input"
                />
              </div>
            )}

            <div
              className="py-2 max-h-[300px] overflow-y-auto"
              role="listbox"
              aria-label="Available databases"
            >
              {filteredDatabases.length === 0 ? (
                <div className="py-4 text-center text-muted-foreground">
                  {searchFilter
                    ? 'No databases match your filter'
                    : 'No databases available'}
                </div>
              ) : (
                filteredDatabases.map((database, index) => (
                  <button
                    key={database}
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    type="button"
                    role="option"
                    aria-selected={index === selectedIndex}
                    onClick={() => handleSelect(database)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors',
                      'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring/30',
                      index === selectedIndex
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/50'
                    )}
                    data-testid={
                      isListingFailure
                        ? `database-selector-fallback-option-${database}`
                        : `database-option-${database}`
                    }
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted">
                      <Database className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <span className="flex-1 min-w-0 font-medium truncate">
                      {database}
                    </span>
                    {isListingFailure && (
                      <UnverifiedDatabaseIndicator
                        kind={fallback.isConfig(database) ? 'config' : 'known'}
                        density="compact"
                        data-testid={`database-selector-dialog-fallback-indicator-${database}`}
                      />
                    )}
                    {index === selectedIndex && (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>

            {/* Set as default — never over unverified rows, and not until the
                revalidation in flight has settled (see `canSetAsDefault`). Rendered
                DISABLED while it waits rather than hidden: the row appearing under
                the list a moment after the dialog opens reads as a glitch, and a
                disabled control with its reason on it is the honest version. */}
            {showSetAsDefault && !isListingFailure && filteredDatabases.length > 0 && (
              <div className="flex items-center gap-2 pt-3 mt-2 border-t">
                <Checkbox
                  id="set-as-default"
                  checked={setAsDefault}
                  disabled={!listingSettled}
                  onCheckedChange={(checked) => setSetAsDefault(checked === true)}
                  data-testid="set-as-default-checkbox"
                />
                <Label
                  htmlFor="set-as-default"
                  className={cn(
                    'text-sm text-muted-foreground',
                    listingSettled ? 'cursor-pointer' : 'cursor-default opacity-60',
                  )}
                >
                  {listingSettled
                    ? 'Set as default database for this connection'
                    : 'Checking the database list…'}
                </Label>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
